import { sanitizeRichHtml } from "@playbrain/content-types/richHtml"
import { useEffect } from "react"

type SpriteFrame = "l45" | "l30" | "l15" | "m" | "r15" | "r30" | "r45"

type ShipProps = {
  controls: string
  ammoLaser: string
  ammoSpread: string
  ammoBomb: string
  ammoRapid: string
}

const spriteFrames: SpriteFrame[] = ["l45", "l30", "l15", "m", "r15", "r30", "r45"]

const projectileClassNames = {
  laser:
    "pointer-events-none absolute z-[1] h-[24px] w-[4px] -translate-x-1/2 rounded-[2px] bg-[linear-gradient(to_bottom,rgba(255,255,255,0.9),var(--cyan,#00e5ff))] shadow-[0_0_6px_var(--cyan,#00e5ff),0_0_14px_var(--cyan,#00e5ff),0_0_28px_rgba(0,229,255,0.4)] after:absolute after:bottom-[-12px] after:left-1/2 after:h-[14px] after:w-[6px] after:-translate-x-1/2 after:rounded-[3px] after:bg-[linear-gradient(to_bottom,var(--cyan,#00e5ff),transparent)] after:opacity-50 after:content-['']",
  bomb: "pointer-events-none absolute z-[1] h-[18px] w-[18px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle_at_40%_35%,#fff_0%,#ffcc00_30%,#ff8800_65%,#cc4400_100%)] shadow-[0_0_8px_#ff8800,0_0_22px_rgba(255,136,0,0.5)]",
  "rapid-laser":
    "pointer-events-none absolute z-[1] h-[12px] w-[2px] -translate-x-1/2 rounded-[1px] bg-[linear-gradient(to_bottom,rgba(255,255,255,0.9),#cc44ff)] shadow-[0_0_4px_#cc44ff,0_0_10px_rgba(204,68,255,0.6)]",
} as const

export function Ship({ controls, ammoLaser, ammoSpread, ammoBomb, ammoRapid }: ShipProps) {
  useEffect(() => {
    const ship = document.querySelector<HTMLElement>("#ship")
    const shipSprite = document.querySelector<HTMLElement>("#calendar-ship-sprite")
    const bulletsContainer = document.querySelector<HTMLElement>("#bullets")
    const controlsHint = document.querySelector<HTMLElement>("#controls-hint")
    const ammoHud = document.querySelector<HTMLElement>("#ammo-hud")
    const fireHud = document.querySelector<HTMLElement>("#fire-hud")
    const ammoLabel = document.querySelector<HTMLElement>("#ammo-label")
    const ammoDots = document.querySelectorAll<HTMLElement>("[data-ammo-dot]")
    const ammoPrev = document.querySelector<HTMLElement>("#ammo-prev")
    const ammoNext = document.querySelector<HTMLElement>("#ammo-next")
    const fireButton = document.querySelector<HTMLElement>("#fire-btn")
    const joystick = document.querySelector<HTMLElement>("#mobile-joystick")
    const joystickThumb = document.querySelector<HTMLElement>("#joystick-thumb")

    const ammoTypes = [
      { id: "laser", label: ammoLaser, color: "#00e5ff", cooldown: 250 },
      { id: "spread", label: ammoSpread, color: "#00ff88", cooldown: 450 },
      { id: "bomb", label: ammoBomb, color: "#ff8800", cooldown: 900 },
      { id: "rapid-laser", label: ammoRapid, color: "#cc44ff", cooldown: 80 },
    ] as const

    let animationFrame = 0
    let collisionFrame = 0
    let placeBackTimer = 0
    let controlsShowTimer = 0
    let controlsHideTimer = 0
    let hudTimer = 0
    let fireHoldTimer = 0
    let shipX = 0
    let targetShipX = 0
    let mouseTargetX = 0
    let keyboardOffsetX = 0
    let shipAngle = 0
    let lastAnimationMs = 0
    let lastCollisionMs = 0
    let lastFiredAt = 0
    let currentAmmoIndex = 0
    let joystickActive = false
    let joystickPointerId = -1
    const keyState: Record<string, boolean> = {}

    function updateAmmoHud() {
      const ammo = ammoTypes[currentAmmoIndex]
      if (ammoLabel) {
        ammoLabel.textContent = ammo.label
        ammoLabel.style.color = ammo.color
      }
      ammoDots.forEach((dot, index) => {
        const active = index === currentAmmoIndex
        dot.style.background = active ? ammo.color : "rgba(255,255,255,0.2)"
        dot.style.color = active ? ammo.color : "transparent"
        dot.classList.toggle("active", active)
      })
    }

    function cycleAmmo(direction: 1 | -1) {
      currentAmmoIndex = (currentAmmoIndex + direction + ammoTypes.length) % ammoTypes.length
      updateAmmoHud()
    }

    function spawnProjectile(viewportX: number, startY: number, cssClass: string, durationMs: number) {
      if (!bulletsContainer) return
      const projectile = document.createElement("div")
      projectile.className = projectileClassNames[cssClass as keyof typeof projectileClassNames] ?? cssClass
      projectile.dataset.type = cssClass
      projectile.dataset.startY = String(startY)
      projectile.style.left = `${viewportX - 50}px`
      bulletsContainer.appendChild(projectile)
      const animation = projectile.animate([{ top: `${startY}px` }, { top: "-40px" }], {
        duration: durationMs,
        easing: "linear",
        fill: "forwards",
      })
      animation.onfinish = () => projectile.remove()
    }

    function shoot() {
      if (!shipSprite) return
      const now = performance.now()
      const ammo = ammoTypes[currentAmmoIndex]
      if (now - lastFiredAt < ammo.cooldown) return
      lastFiredAt = now

      const rect = shipSprite.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const topY = rect.top

      if (ammo.id === "spread") {
        const offset = rect.width / 2 - 6
        spawnProjectile(centerX - offset, topY, "laser", 520)
        spawnProjectile(centerX, topY, "laser", 490)
        spawnProjectile(centerX + offset, topY, "laser", 520)
        return
      }

      spawnProjectile(
        centerX,
        topY,
        ammo.id,
        ammo.id === "bomb" ? 1100 : ammo.id === "rapid-laser" ? 260 : 500
      )
    }

    function spawnImpact(x: number, y: number) {
      const impact = document.createElement("div")
      impact.className =
        "fixed z-[200] h-[6px] w-[6px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-[0_0_8px_4px_var(--cyan,#00e5ff)] pointer-events-none"
      impact.dataset.shipImpact = "true"
      impact.style.left = `${x}px`
      impact.style.top = `${y}px`
      document.body.appendChild(impact)
      const animation = impact.animate(
        [
          { scale: "1", opacity: "1" },
          { scale: "3", opacity: "0" },
        ],
        {
          duration: 300,
          easing: "ease-out",
          fill: "forwards",
        }
      )
      animation.onfinish = () => impact.remove()
    }

    function collisionLoop(timestamp: number) {
      collisionFrame = requestAnimationFrame(collisionLoop)
      if (timestamp - lastCollisionMs < 1000 / 60 || !bulletsContainer) return
      lastCollisionMs = timestamp

      for (const event of document.querySelectorAll<HTMLElement>(".event")) {
        const eventRect = event.getBoundingClientRect()
        for (const bullet of Array.from(bulletsContainer.children) as HTMLElement[]) {
          const bulletRect = bullet.getBoundingClientRect()
          const startY = Number(bullet.dataset.startY)
          if (bulletRect.top > startY - 80) continue
          if (bullet.dataset.hit) continue
          const colliding = !(
            eventRect.right < bulletRect.left ||
            eventRect.left > bulletRect.right ||
            eventRect.bottom < bulletRect.top ||
            eventRect.top > bulletRect.bottom
          )
          if (!colliding) continue

          bullet.dataset.hit = "1"
          spawnImpact(bulletRect.left + bulletRect.width / 2, bulletRect.top + bulletRect.height / 2)
          bullet.animate([{ opacity: "1" }, { opacity: "0" }], {
            duration: 150,
            easing: "ease-out",
            fill: "forwards",
          })
          event.classList.remove("hit-shake")
          void event.offsetWidth
          event.classList.add("hit-shake")
          break
        }
      }
    }

    function animationLoop(timestamp: number) {
      animationFrame = requestAnimationFrame(animationLoop)
      if (timestamp - lastAnimationMs < 1000 / 60) return
      lastAnimationMs = timestamp

      if (keyState.ArrowLeft) keyboardOffsetX -= 8
      else if (keyState.ArrowRight) keyboardOffsetX += 8
      else keyboardOffsetX *= 0.85

      const halfPage = (document.body.clientWidth - 50) / 2 - 60
      targetShipX = Math.min(Math.max(mouseTargetX + keyboardOffsetX, -halfPage), halfPage)
      const nextShipX = (shipX * 5 + targetShipX) / 6
      const delta = nextShipX - shipX
      shipX = nextShipX
      shipAngle = delta / 5 + (shipAngle * 9) / 10
      const spriteIndex = Math.round(Math.min(Math.max(shipAngle, -3), 3)) + 3

      ship?.style.setProperty("--ship-x", `${shipX}px`)
      if (shipSprite) {
        shipSprite.classList.remove(...spriteFrames)
        shipSprite.classList.add(spriteFrames[spriteIndex])
      }
    }

    function onMouseMove(event: MouseEvent) {
      mouseTargetX = event.clientX - document.body.clientWidth / 2
    }

    function onMouseClick(event: MouseEvent) {
      if ((event as PointerEvent).pointerType === "touch") return
      shoot()
    }

    function onKeyDown(event: KeyboardEvent) {
      if ([" ", "ArrowLeft", "ArrowRight"].includes(event.key)) event.preventDefault()
      keyState[event.key] = true
      if (event.key === " ") shoot()
      if (event.key === "q" || event.key === "Q") cycleAmmo(-1)
      if (event.key === "e" || event.key === "E") cycleAmmo(1)
    }

    function onKeyUp(event: KeyboardEvent) {
      keyState[event.key] = false
    }

    function updateJoystick(clientX: number) {
      if (!joystick || !joystickThumb) return
      const radius = joystick.offsetWidth / 2
      const rect = joystick.getBoundingClientRect()
      const dx = Math.max(-radius, Math.min(radius, clientX - (rect.left + rect.width / 2)))
      joystickThumb.style.translate = `calc(-50% + ${dx}px) -50%`
      mouseTargetX = (dx / radius) * ((document.body.clientWidth - 50) / 2 - 60)
      keyboardOffsetX = 0
    }

    function onJoystickDown(event: PointerEvent) {
      event.stopPropagation()
      joystick?.setPointerCapture(event.pointerId)
      joystickActive = true
      joystickPointerId = event.pointerId
      if (joystickThumb) joystickThumb.style.transition = "none"
      updateJoystick(event.clientX)
    }

    function onJoystickMove(event: PointerEvent) {
      if (!joystickActive || event.pointerId !== joystickPointerId) return
      event.stopPropagation()
      updateJoystick(event.clientX)
    }

    function onJoystickUp(event: PointerEvent) {
      if (event.pointerId !== joystickPointerId) return
      event.stopPropagation()
      joystickActive = false
      joystickPointerId = -1
      if (joystickThumb) {
        joystickThumb.style.transition = "translate 0.12s ease"
        joystickThumb.style.translate = "-50% -50%"
      }
      mouseTargetX = shipX
      keyboardOffsetX = 0
    }

    function pulseFire() {
      if (!fireButton) return
      fireButton.classList.remove("firing")
      void fireButton.offsetWidth
      fireButton.classList.add("firing")
    }

    function onFireButtonDown(event: PointerEvent) {
      event.stopPropagation()
      shoot()
      pulseFire()
      window.clearInterval(fireHoldTimer)
      fireHoldTimer = window.setInterval(
        () => {
          shoot()
          pulseFire()
        },
        Math.max(ammoTypes[currentAmmoIndex].cooldown, 80)
      )
    }

    function onFireButtonUp(event: Event) {
      event.stopPropagation()
      window.clearInterval(fireHoldTimer)
    }

    function onAmmoPrev() {
      cycleAmmo(-1)
    }

    function onAmmoNext() {
      cycleAmmo(1)
    }

    ship?.classList.remove("place-back")
    updateAmmoHud()
    controlsShowTimer = window.setTimeout(() => controlsHint?.classList.add("visible"), 1200)
    controlsHideTimer = window.setTimeout(() => controlsHint?.classList.remove("visible"), 4000)
    hudTimer = window.setTimeout(() => {
      ammoHud?.classList.add("visible")
      fireHud?.classList.add("visible")
      joystick?.classList.add("visible")
    }, 1400)
    placeBackTimer = window.setTimeout(() => ship?.classList.add("place-back"), 50)

    animationFrame = requestAnimationFrame(animationLoop)
    collisionFrame = requestAnimationFrame(collisionLoop)
    document.addEventListener("mousemove", onMouseMove)
    document.addEventListener("keydown", onKeyDown)
    document.addEventListener("keyup", onKeyUp)
    document.addEventListener("click", onMouseClick)
    ammoPrev?.addEventListener("click", onAmmoPrev)
    ammoNext?.addEventListener("click", onAmmoNext)
    fireButton?.addEventListener("pointerdown", onFireButtonDown)
    fireButton?.addEventListener("pointerup", onFireButtonUp)
    fireButton?.addEventListener("pointerleave", onFireButtonUp)
    joystick?.addEventListener("pointerdown", onJoystickDown)
    joystick?.addEventListener("pointermove", onJoystickMove)
    joystick?.addEventListener("pointerup", onJoystickUp)
    joystick?.addEventListener("pointercancel", onJoystickUp)

    return () => {
      cancelAnimationFrame(animationFrame)
      cancelAnimationFrame(collisionFrame)
      window.clearTimeout(placeBackTimer)
      window.clearTimeout(controlsShowTimer)
      window.clearTimeout(controlsHideTimer)
      window.clearTimeout(hudTimer)
      window.clearInterval(fireHoldTimer)
      document.removeEventListener("mousemove", onMouseMove)
      document.removeEventListener("keydown", onKeyDown)
      document.removeEventListener("keyup", onKeyUp)
      document.removeEventListener("click", onMouseClick)
      ammoPrev?.removeEventListener("click", onAmmoPrev)
      ammoNext?.removeEventListener("click", onAmmoNext)
      fireButton?.removeEventListener("pointerdown", onFireButtonDown)
      fireButton?.removeEventListener("pointerup", onFireButtonUp)
      fireButton?.removeEventListener("pointerleave", onFireButtonUp)
      joystick?.removeEventListener("pointerdown", onJoystickDown)
      joystick?.removeEventListener("pointermove", onJoystickMove)
      joystick?.removeEventListener("pointerup", onJoystickUp)
      joystick?.removeEventListener("pointercancel", onJoystickUp)
      document.querySelectorAll("[data-ship-impact]").forEach((impact) => impact.remove())
      bulletsContainer?.replaceChildren()
    }
  }, [ammoBomb, ammoLaser, ammoRapid, ammoSpread])

  return (
    <>
      <div className="pointer-events-none absolute top-0 left-0 z-[99] h-full w-[calc(100vw-50px-1rem)] overflow-hidden">
        <div
          className="pointer-events-none fixed top-[100lvh] left-0 grid h-full w-full translate-x-[var(--ship-x)] content-start justify-center transition-[top] duration-1000 ease-out [--ship-x:0px] [filter:drop-shadow(50px_30px_0_rgba(0,0,0,0.5))] [&.place-back]:top-[87svh] [&.place-back_#calendar-ship-sprite]:[--sprite-scale:100px] [@media(pointer:coarse)]:[&.place-back_#calendar-ship-sprite]:[--sprite-scale:70px]"
          data-id="calendar-ship"
          id="ship"
        >
          <div
            className="m relative h-[var(--sprite-scale)] w-[var(--sprite-scale)] bg-[length:auto_100%] bg-[url('/images/calendar/ship_sprite.png')] transition-[width,height] duration-200 ease-in-out [--sprite-scale:256px] [animation:idleBob_2.5s_ease-in-out_infinite] [background-position:-768px_0] [&.l15]:[background-position-x:calc(var(--sprite-scale)*-2)] [&.l30]:[background-position-x:calc(var(--sprite-scale)*-1)] [&.l45]:[background-position-x:0] [&.m]:[background-position-x:calc(var(--sprite-scale)*-3)] [&.r15]:[background-position-x:calc(var(--sprite-scale)*-4)] [&.r30]:[background-position-x:calc(var(--sprite-scale)*-5)] [&.r45]:[background-position-x:calc(var(--sprite-scale)*-6)]"
            data-id="calendar-ship-sprite"
            id="calendar-ship-sprite"
          />
        </div>
        <div
          className="fixed top-0 left-[50px] h-[100dvh]"
          id="bullets"
        />
      </div>
      <div
        className="pointer-events-none fixed bottom-[8rem] left-1/2 z-[100] -translate-x-1/2 rounded-full border border-[rgba(255,255,255,0.15)] bg-[rgba(0,0,0,0.5)] px-[1em] py-[0.4em] text-[0.75rem] text-[rgba(255,255,255,0.7)] tracking-[0.05em] opacity-0 backdrop-blur-[6px] transition-opacity duration-500 ease-in-out [&.visible]:opacity-100 [&_span]:font-bold [&_span]:text-[var(--cyan,#00e5ff)]"
        id="controls-hint"
        dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(controls) }}
      />
      <div
        className="pointer-events-auto fixed right-[2rem] bottom-[1.5rem] z-[100] flex flex-col items-center gap-[0.5rem] border border-[rgba(255,255,255,0.1)] bg-[rgba(0,0,0,0.55)] px-[1rem] py-[0.65rem] opacity-0 transition-opacity duration-500 ease-in-out [&.visible]:opacity-100 [@media(pointer:coarse)]:top-[1.5rem] [@media(pointer:coarse)]:right-[1rem] [@media(pointer:coarse)]:bottom-auto [@media(pointer:coarse)]:gap-[0.7rem] [@media(pointer:coarse)]:px-[1.2rem] [@media(pointer:coarse)]:py-[0.8rem]"
        data-ammo-bomb={ammoBomb}
        data-ammo-laser={ammoLaser}
        data-ammo-rapid={ammoRapid}
        data-ammo-spread={ammoSpread}
        id="ammo-hud"
      >
        <div className="flex items-center gap-[0.6rem]">
          <span
            className="cursor-pointer touch-manipulation select-none border border-[rgba(255,255,255,0.3)] bg-[rgba(255,255,255,0.12)] px-[0.8em] py-[0.35em] font-bold text-[0.65rem] text-[rgba(255,255,255,0.75)] tracking-[0.05em] transition-[background] duration-150 ease-in-out [-webkit-tap-highlight-color:transparent] active:bg-[rgba(255,255,255,0.28)] [@media(pointer:coarse)]:px-[1em] [@media(pointer:coarse)]:py-[0.5em] [@media(pointer:coarse)]:text-[0.8rem]"
            id="ammo-prev"
          >
            <span className="[@media(pointer:coarse)]:hidden">Q</span>
            <span className="[@media(pointer:fine)]:hidden">←</span>
          </span>
          <span
            className="min-w-[4rem] text-center font-bold text-[0.85rem] text-[var(--cyan,#00e5ff)] tracking-[0.14em] transition-colors duration-200 ease-in-out [text-shadow:0_0_10px_currentColor] [@media(pointer:coarse)]:min-w-[5rem] [@media(pointer:coarse)]:text-[1rem]"
            id="ammo-label"
          >
            {ammoLaser}
          </span>
          <span
            className="cursor-pointer touch-manipulation select-none border border-[rgba(255,255,255,0.3)] bg-[rgba(255,255,255,0.12)] px-[0.8em] py-[0.35em] font-bold text-[0.65rem] text-[rgba(255,255,255,0.75)] tracking-[0.05em] transition-[background] duration-150 ease-in-out [-webkit-tap-highlight-color:transparent] active:bg-[rgba(255,255,255,0.28)] [@media(pointer:coarse)]:px-[1em] [@media(pointer:coarse)]:py-[0.5em] [@media(pointer:coarse)]:text-[0.8rem]"
            id="ammo-next"
          >
            <span className="[@media(pointer:coarse)]:hidden">E</span>
            <span className="[@media(pointer:fine)]:hidden">→</span>
          </span>
        </div>
        <div
          className="flex gap-[7px]"
          id="ammo-dots"
        >
          <span
            className="h-[7px] w-[7px] rounded-full border border-[rgba(255,255,255,0.2)] bg-[rgba(255,255,255,0.25)] transition-[background,transform,box-shadow] duration-200 ease-in-out [&.active]:scale-[1.6] [&.active]:shadow-[0_0_6px_1px_currentColor]"
            data-ammo-dot
          />
          <span
            className="h-[7px] w-[7px] rounded-full border border-[rgba(255,255,255,0.2)] bg-[rgba(255,255,255,0.25)] transition-[background,transform,box-shadow] duration-200 ease-in-out [&.active]:scale-[1.6] [&.active]:shadow-[0_0_6px_1px_currentColor]"
            data-ammo-dot
          />
          <span
            className="h-[7px] w-[7px] rounded-full border border-[rgba(255,255,255,0.2)] bg-[rgba(255,255,255,0.25)] transition-[background,transform,box-shadow] duration-200 ease-in-out [&.active]:scale-[1.6] [&.active]:shadow-[0_0_6px_1px_currentColor]"
            data-ammo-dot
          />
          <span
            className="h-[7px] w-[7px] rounded-full border border-[rgba(255,255,255,0.2)] bg-[rgba(255,255,255,0.25)] transition-[background,transform,box-shadow] duration-200 ease-in-out [&.active]:scale-[1.6] [&.active]:shadow-[0_0_6px_1px_currentColor]"
            data-ammo-dot
          />
        </div>
      </div>
      <div
        className="pointer-events-auto fixed right-[2rem] bottom-[1.5rem] z-[100] opacity-0 transition-opacity duration-500 ease-in-out [&.visible]:opacity-100 [@media(pointer:coarse)]:right-[2.5rem] [@media(pointer:fine)]:hidden"
        id="fire-hud"
      >
        <button
          className="flex h-[4rem] w-[4rem] cursor-pointer touch-manipulation select-none items-center justify-center rounded-full border-2 border-[rgba(255,255,255,0.35)] bg-[rgba(255,255,255,0.08)] p-0 text-[1.4rem] text-[rgba(255,255,255,0.8)] transition-[background,border-color,scale] duration-100 ease-in-out [-webkit-tap-highlight-color:transparent] active:scale-[0.9] active:border-[rgba(255,255,255,0.9)] active:bg-[rgba(255,255,255,0.35)] [&.firing]:[animation:firePulse_0.18s_ease-out] [@media(pointer:coarse)]:h-[5.5rem] [@media(pointer:coarse)]:w-[5.5rem] [@media(pointer:coarse)]:text-[2rem]"
          id="fire-btn"
          type="button"
          aria-label="Fire"
        >
          ●
        </button>
      </div>
      <div
        className="pointer-events-auto fixed bottom-[1.5rem] left-[calc(50px_+_2rem)] z-[100] h-[84px] w-[84px] touch-none rounded-full border-2 border-[rgba(255,255,255,0.22)] bg-[rgba(0,0,0,0.45)] opacity-0 transition-opacity duration-500 ease-in-out [&.visible]:opacity-100 [@media(pointer:coarse)]:h-[100px] [@media(pointer:coarse)]:w-[100px] [@media(pointer:fine)]:hidden"
        id="mobile-joystick"
      >
        <div
          className="pointer-events-none absolute top-1/2 left-1/2 h-[38px] w-[38px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[rgba(255,255,255,0.6)] bg-[rgba(255,255,255,0.3)] transition-transform duration-[60ms] ease-in-out [@media(pointer:coarse)]:h-[46px] [@media(pointer:coarse)]:w-[46px]"
          id="joystick-thumb"
        />
      </div>
    </>
  )
}

export default Ship
